// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isDevelopment } from '@shared/environment';
import { type ReactNode, useEffect, useMemo } from 'react';
import {
  GlyphsMap,
  type GlyphHex,
  type GlyphDecimal,
  type GlyphId,
  type GlyphName,
} from './glyphs';

interface BaseGlyphProps {
  /**
   * How many times to emit the resolved HTML reference string as sibling text
   * nodes.
   *
   * @defaultValue 1
   */
  repeat?: number;
}

/**
 * Lookup by map key: {@link GlyphName} alias, enum-style `id`, or string matching
 * {@link GlyphsMap}.
 *
 * @remarks
 * Prefer alias keys (e.g. from `GlyphNames`) or member names present in the map.
 */
interface GlyphIdProps extends BaseGlyphProps {
  id:
    | GlyphId
    | GlyphName
    | GlyphDecimal
    | GlyphHex
    | `${GlyphId}`
    | `${GlyphDecimal}`
    | `${GlyphHex}`;
}

/**
 * Lookup by decimal code point; the map key is `String(decimal)`.
 *
 * @see {@link GlyphsMap}
 */
interface GlyphDecimalProps extends BaseGlyphProps {
  decimal: number | `${number}`;
}

/**
 * Lookup by hex scalar: input is normalized (trim, strip `&#x` / `U+` / `#` / `;`,
 * drop non-hex), then uppercased to match {@link GlyphsMap} keys.
 *
 * @see {@link GlyphsMap}
 */
interface GlyphHexProps extends BaseGlyphProps {
  hex: string;
}

/**
 * Exactly one branch is active: `id`, `decimal`, or `hex`. Omitted or unknown map
 * keys produce `null` for the resolved string.
 *
 * @remarks
 * In development, {@link isDevelopment} is true, and a missing map entry logs
 * `console.warn` with the props object; production does not log.
 * @example Alias key
 *
 * ```tsx
 * <Glyph id="Blank" />;
 * ```
 *
 * @example Decimal code point
 *
 * ```tsx
 * <Glyph decimal={160} />;
 * ```
 *
 * @example Hex (normalized to uppercase digits for lookup)
 *
 * ```tsx
 * <Glyph hex="&#x00A0;" />;
 * ```
 *
 * @see {@link GlyphsMap}
 */
export type GlyphProps = GlyphIdProps | GlyphDecimalProps | GlyphHexProps;

/**
 * Resolves {@link GlyphProps} through {@link GlyphsMap} and builds `content` as
 * repeated text segments.
 *
 * @remarks
 * Warnings only when {@link isDevelopment} is true and resolution fails.
 * @example
 *
 * ```tsx
 * const { content, glyphHtml } = useGlyph({ id: 'Blank', repeat: 2 });
 * ```
 *
 * @param props - Discriminated props (`id` | `decimal` | `hex`). Optional `repeat`
 *   defaults to `1` when omitted (see `@defaultValue` on `repeat` in props types).
 * @returns An object with:
 *
 *   - `glyphHtml` — The HTML character reference string from the map (e.g. `&nbsp;`),
 *       or `null` if the key is missing.
 *   - `content` — `null` if `glyphHtml` is `null`; otherwise an array of `repeat` text
 *       nodes, each equal to `glyphHtml`.
 *
 * @see {@link GlyphsMap}
 */
export const useGlyph = ({ repeat = 1, ...props }: GlyphProps) => {
  const glyphHtml = useMemo(() => {
    if ('decimal' in props) {
      return GlyphsMap.get(props.decimal.toString()) ?? null;
    }

    if ('hex' in props) {
      const value = props.hex
        .trim()
        .replace(/^&#x/gi, '')
        .replace(/^U\+/gi, '')
        .replace(/^#/, '')
        .replace(/;$/g, '')
        .replace(/[^0-9a-f]/gi, '')
        .toUpperCase();

      return GlyphsMap.get(value) ?? null;
    }

    if ('id' in props) {
      return GlyphsMap.get(props.id as string) ?? null;
    }

    return null;
  }, [props]);

  const content = useMemo<ReactNode | null>(() => {
    if (glyphHtml === null) {
      return null;
    }

    return Array.from({ length: repeat }, () => glyphHtml);
  }, [glyphHtml, repeat]);

  useEffect(() => {
    if (isDevelopment && glyphHtml === null) {
      console.warn('[Glyph] Unknown reference (not in GlyphsMap):', props);
    }
  }, [glyphHtml, props]);

  return {
    content,
    glyphHtml,
  };
};
